/**
 * Sprint 11 Phase 3d — super-admin telemetry summary.
 *
 * The 90-day nudge dot is a passive signal-generator. This page
 * surfaces what it's collected so the team can answer "is the
 * 90-day re-assessment moment real for coaches yet?" in 10 seconds.
 *
 * Hidden behind a super-admin role check — the data is internal
 * product analytics, not coach-facing.
 *
 * Realistic expectation: signal arrives in months, not weeks. The
 * earliest possible hover event requires a real client engagement
 * to cross its 90-day mark — months out from first send. This page
 * exists so that when signal does arrive, it's already visible
 * without instrumenting anything new.
 */
import React from "react";
import { Link, Navigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

const SURFACE = "nudge_dot_90day";

export default function TelemetrySummary() {
  const { user, loading } = useAuth();
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/api/telemetry/summary/${SURFACE}`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setErr(formatApiError(e));
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") {
    return (
      <main className="p-8" data-testid="telemetry-forbidden">
        <p className="text-sm" style={{ color: "#AD393E" }}>
          Telemetry summary is super-admin only.
        </p>
        <Link to="/dashboard" className="text-sm underline">Back to home</Link>
      </main>
    );
  }

  const totalEvents = data?.total_events ?? null;
  const byKind = data?.by_kind ?? [];

  return (
    <main className="p-8 max-w-3xl" data-testid="telemetry-summary-page">
      <div className="mb-6">
        <Link to="/dashboard" className="text-xs uppercase tracking-widest" style={{ color: "#6F6F6F" }}>
          ← All deployments
        </Link>
      </div>
      <h1 className="text-2xl mb-1" style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A" }}>
        90-day nudge dot · telemetry
      </h1>
      <p className="text-sm mb-6 max-w-prose" style={{ color: "#6F6F6F" }}>
        Coaches see an amber dot on the IndividualClientView header when a client's last
        report was sent 90+ days ago. The dot has no action — clicking does not navigate or
        schedule anything. We log hover (once per session) and click separately. If coaches
        never engage the dot, the 90-day re-assessment moment isn't real for them yet and
        Phase 3d longitudinal infra shouldn't be built. If they engage, demand-led
        validation is in the bank.
      </p>

      {err && (
        <div className="text-sm mb-4" style={{ color: "#AD393E" }} data-testid="telemetry-error">
          {err}
        </div>
      )}

      {data && (
        <section
          className="p-5"
          style={{ background: "#FBFAF7", border: "1px solid #E0E0E0" }}
          data-testid="telemetry-summary-card"
        >
          <div className="eyebrow mb-3" style={{ color: "#2563EB" }}>
            Surface · {data.surface}
          </div>
          <div className="flex items-baseline gap-3 mb-5">
            <div className="text-4xl" style={{ fontFamily: "Oswald", fontWeight: 700 }} data-testid="telemetry-total-events">
              {totalEvents}
            </div>
            <div className="text-sm" style={{ color: "#6F6F6F" }}>
              total events recorded
            </div>
          </div>

          <table className="text-sm w-full" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #E0E0E0" }}>
                <th className="text-left py-2 pr-4" style={{ fontFamily: "Oswald", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#6F6F6F" }}>Kind</th>
                <th className="text-right py-2 pr-4" style={{ fontFamily: "Oswald", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#6F6F6F" }}>Events</th>
                <th className="text-right py-2" style={{ fontFamily: "Oswald", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#6F6F6F" }}>Unique coaches</th>
              </tr>
            </thead>
            <tbody>
              {byKind.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-4 text-center" style={{ color: "#9A9A9A" }} data-testid="telemetry-empty">
                    No events yet — patient signal, no data until a real engagement crosses 90 days.
                  </td>
                </tr>
              ) : (
                byKind.map((row) => (
                  <tr key={row.kind} style={{ borderBottom: "1px solid #F0F0F0" }} data-testid={`telemetry-row-${row.kind}`}>
                    <td className="py-3 pr-4 text-ink">{row.kind}</td>
                    <td className="py-3 pr-4 text-right">{row.events}</td>
                    <td className="py-3 text-right">{row.unique_users}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      )}
    </main>
  );
}
